import { useEffect, useRef, useState, useMemo } from "react";
import { api } from "../lib/api";

/**
 * useLivePrices — real-time price ticker for a list of symbols.
 *
 * Polls /api/prices every 30s (matches server cache TTL). Returns a
 * { [symbol]: { price, change, change_pct, prev_close, high, low, as_of } }
 * map. Missing symbols are simply absent from the map — callers should
 * fall back to entry_price / stored value when a symbol is not yet in
 * the map.
 *
 * @param {string[]} symbols — list of tickers to track. Order-insensitive.
 * @param {object}   opts    — { intervalMs, enabled }
 * @returns { prices: Object, lastFetch: Date|null, isPolling: bool }
 */
export function useLivePrices(symbols, opts = {}) {
  const { intervalMs = 30_000, enabled = true } = opts;
  const [prices, setPrices] = useState({});
  const [lastFetch, setLastFetch] = useState(null);
  const inFlightRef = useRef(false);

  // Normalize + dedupe so useEffect only fires when the SET of symbols changes,
  // not every render.
  const key = useMemo(() => {
    if (!symbols || !symbols.length) return "";
    return [...new Set(symbols.map(s => (s || "").toUpperCase()).filter(Boolean))].sort().join(",");
  }, [symbols]);

  useEffect(() => {
    if (!enabled || !key) return;

    let cancelled = false;

    const fetchOnce = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const r = await api.get(`/prices?symbols=${key}`);
        if (!cancelled && r?.data?.quotes) {
          setPrices(r.data.quotes);
          setLastFetch(new Date());
        }
      } catch (e) {
        // Silent — the entry-price fallback in the UI handles this gracefully.
      } finally {
        inFlightRef.current = false;
      }
    };

    fetchOnce();
    const id = setInterval(fetchOnce, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
      // Reset in-flight so React StrictMode's double-invocation (dev) or
      // fast remounts don't leave the guard permanently stuck at true,
      // which would silently block every subsequent poll.
      inFlightRef.current = false;
    };
  }, [key, intervalMs, enabled]);

  return { prices, lastFetch, isPolling: enabled && !!key };
}

/**
 * Overlays live quote data onto a stored alert `live` block. Preserves
 * everything the server computed and only OVERRIDES the fields the live
 * ticker knows: current_price and day_change_pct. Recomputes return_pct
 * from the new current_price + entry_price so downstream renders (green
 * / red pulsing) match the ticking price.
 */
export function mergeLiveIntoAlert(alert, livePrices) {
  const q = livePrices?.[alert.symbol];
  const live = alert.live || {};
  if (!q) return live;

  const entry = Number(alert.entry_price || 0);
  const price = Number(q.price);
  const return_pct = entry > 0
    ? Number((((price - entry) / entry) * 100).toFixed(2))
    : live.return_pct;

  return {
    ...live,
    current_price: price,
    day_change_pct: q.change_pct,
    return_pct,
    prev_close: q.prev_close,
    live_source: "finnhub",
    live_as_of: q.as_of,
  };
}
